import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Divider, Stack, Typography } from '@mui/material';
import {
  Description as DescriptionIcon,
  PictureAsPdf as PdfIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import StoreCustomerSectionCard from './StoreCustomerSectionCard';
import { getConsentFormErrorKey, getConsentFormName } from '../consent-forms/consentFormDisplay';
import { getConsentRequestFeedbackKey } from '../consent-forms/AppointmentConsentStatus';
import { useToast } from '../../context/ToastContext';
import ConsentFormService from '../../services/consentFormService';
import { formatCalendarDate, formatUtcDateTime } from '../../utils/dateFormatUtils';
import { BRAND, brandAlpha } from '../../theme/brandTokens';

const brandButtonSx = {
  textTransform: 'none',
  fontWeight: 700,
  color: BRAND.accentText,
};

const saveBlob = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

/**
 * The consent forms a customer accepted, each with a downloadable certificate for refund
 * disputes, and a way to send a link for forms still pending on what they hold.
 */
const StoreCustomerConsentsCard = ({ storeId, customerId, canSend = true, hideWhenEmpty = false, sx }) => {
  const { t, i18n } = useTranslation();
  const { showError, showInfo, showSuccess } = useToast();
  const [acceptances, setAcceptances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [sending, setSending] = useState(false);

  const loadAcceptances = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setAcceptances(await ConsentFormService.getCustomerAcceptances(storeId, customerId));
    } catch (loadError) {
      console.error('Failed to load consent acceptances:', loadError);
      setError('consentForms.errors.acceptances');
    } finally {
      setLoading(false);
    }
  }, [customerId, storeId]);

  useEffect(() => {
    loadAcceptances();
  }, [loadAcceptances]);

  const handleDownload = async (acceptance) => {
    try {
      setDownloadingId(acceptance.id);
      const blob = await ConsentFormService.downloadAcceptanceCertificate(storeId, acceptance.id);
      saveBlob(blob, `consent-certificate-${acceptance.id}.pdf`);
    } catch (downloadError) {
      console.error('Failed to download consent certificate:', downloadError);
      showError(t('consentForms.errors.certificate'));
    } finally {
      setDownloadingId(null);
    }
  };

  const handleSend = async () => {
    try {
      setSending(true);
      const result = await ConsentFormService.sendConsentRequest(storeId, { customerId });
      const key = getConsentRequestFeedbackKey(result);
      if (result.emailSent || result.smsSent) {
        showSuccess(t(key));
      } else {
        showInfo(t(key));
      }
    } catch (sendError) {
      console.error('Failed to send consent request:', sendError);
      showError(t(getConsentFormErrorKey(sendError, 'consentForms.errors.sendRequest')));
    } finally {
      setSending(false);
    }
  };

  if (hideWhenEmpty && !loading && acceptances.length === 0) {
    return null;
  }

  const sendButton = canSend ? (
    <Button
      id="customer-consents-send"
      size="small"
      startIcon={sending ? <CircularProgress size={14} /> : <SendIcon fontSize="small" />}
      onClick={handleSend}
      disabled={sending}
      sx={brandButtonSx}
    >
      {sending ? t('consentForms.staff.sending') : t('consentForms.staff.sendLink')}
    </Button>
  ) : null;

  return (
    <Box id="customer-consents" sx={sx}>
      <StoreCustomerSectionCard
        title={t('consentForms.customerCard.title')}
        icon={<DescriptionIcon fontSize="small" />}
        action={sendButton}
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {t('consentForms.customerCard.subtitle')}
        </Typography>
        {loading ? (
          <CircularProgress size={20} sx={{ color: BRAND.accentText }} />
        ) : error ? (
          <Typography variant="body2" color="error">{t(error)}</Typography>
        ) : acceptances.length === 0 ? (
          <Typography variant="body2" color="text.secondary">{t('consentForms.customerCard.empty')}</Typography>
        ) : (
          <Stack divider={<Divider flexItem sx={{ borderColor: brandAlpha('accent', 0.12) }} />} spacing={1.25}>
            {acceptances.map((acceptance) => (
              <Stack
                key={acceptance.id}
                id={`customer-consent-${acceptance.id}`}
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                spacing={1}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: BRAND.text, wordBreak: 'break-word' }}>
                    {`${getConsentFormName(acceptance.formName, i18n.language, t)} · ${t('consentForms.customerCard.version', { version: acceptance.versionNumber })}`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {t('consentForms.customerCard.acceptedAt', { date: formatUtcDateTime(acceptance.acceptedAtUtc) })}
                    {' · '}
                    {t(`consentForms.customerCard.context.${acceptance.contextType}`, { defaultValue: '' })}
                    {acceptance.appointmentDate
                      ? ` · ${t('consentForms.customerCard.appointment', { date: formatCalendarDate(acceptance.appointmentDate) })}`
                      : ''}
                  </Typography>
                </Box>
                <Button
                  id={`customer-consent-certificate-${acceptance.id}`}
                  size="small"
                  startIcon={downloadingId === acceptance.id ? <CircularProgress size={14} /> : <PdfIcon fontSize="small" />}
                  onClick={() => handleDownload(acceptance)}
                  disabled={downloadingId === acceptance.id}
                  sx={{ ...brandButtonSx, flexShrink: 0 }}
                >
                  {t('consentForms.customerCard.certificate')}
                </Button>
              </Stack>
            ))}
          </Stack>
        )}
      </StoreCustomerSectionCard>
    </Box>
  );
};

export default StoreCustomerConsentsCard;
